
// ---------------------------------------------------------------------------------------------------------------
// input.js — keyboard / pointer / touch / gamepad input mapped onto Game actions.
//
// Public names: DEFAULT_BINDS, HOLD_ACTIONS, Input (plus the statics Input.RESERVED_CODES, Input.isBindableCode,
// Input.sanitizeBindings used by main.js for rebind validation and persisted bindings).
// Depends on: Game (rules.js) via the instance passed to the constructor (onFlipper, onPlunge, onStart, onNudge).
//
// Three sources (keys, pointers, gamepad) are merged per action: a "hold" action (flipL/flipR/plunge) stays active
// while ANY source holds it, and only calls into the Game when the merged state actually changes. Momentary actions
// (start, nudges) fire on the press edge only. Every key code can be remapped at runtime (beginCapture) and the
// bindings object is plain JSON so main.js can persist it.
//
// UI etiquette (see the hooks): keys are never taken away from a focused button/input (Enter, Space and Tab reach
// the control), hotkeys ignore Ctrl/Cmd/Alt combinations, game actions are ignored while the help panel is open
// (hooks.inputBlocked() === 'help') and every key except Escape is swallowed in the machine viewer ('machine').
// Pointers ignore non-primary mouse buttons, the pause overlay and a paused game. A release the player did not
// perform (focus loss, pause, remap, context menu) cancels a plunger pull instead of launching the ball.
// ---------------------------------------------------------------------------------------------------------------

/** Default key bindings: action -> up to four KeyboardEvent.code values. */
const DEFAULT_BINDS = {
  flipL: ["ShiftLeft"],
  flipR: ["ShiftRight"],
  flipU: [],
  plunge: ["Enter"],
  start: ["Digit1"],
  nudgeL: ["Slash"],
  nudgeR: ["KeyZ"],
  nudgeU: ["Space"],
};

/** Actions with a held state (press = on, release = off); everything else is edge-triggered. */
const HOLD_ACTIONS = ["flipL", "flipR", "flipU", "plunge"];

class Input {
  /**
   * @param {Game} game   rules instance receiving onFlipper/onPlunge/onStart/onNudge
   * @param {object} hooks optional UI callbacks: onPause, onView, onMute, onHelp, onFullscreen, onDebug, onRestart,
   *                       onAnyInput (audio unlock), onRemapped(action, outcome) with outcome 'bound' | 'cancelled' |
   *                       'reserved', onEscape() -> true when the UI consumed Escape, inputBlocked() -> 'help' |
   *                       'machine' | false, isPaused() -> boolean (pointers are ignored while paused)
   */
  constructor(game, hooks = {}) {
    this.game = game;
    this.hooks = hooks;
    this.binds = JSON.parse(JSON.stringify(DEFAULT_BINDS));
    this.down = new Set(); // key codes currently held
    this.active = { flipL: false, flipR: false, flipU: false, plunge: false };
    this.remote = new Set();
    this.stageTimers = new Map();
    this.secondStages = new Set(); // merged hold-action state sent to the Game
    this.pointers = new Map(); // pointerId -> hold action it is driving
    this.captureFor = null; // action name while waiting for a key to remap, else null
    this.gpFlipL = false;
    this.gpFlipR = false;
    this.gpPlunge = false;
    this.nudgeCD = 0; // performance.now() timestamp before which nudges are ignored
    this._gpDead = false; // set once navigator.getGamepads throws (e.g. blocked by permissions policy)
    this._gpStart = false; // previous frame's gamepad Start button state (edge detection)
    this._gpNudgeX = false; // stick latches: a nudge fires once per deflection past 0.8, re-armed below 0.4
    this._gpNudgeY = false;
    addEventListener("keydown", (event) => this.onKey(event, true), { capture: true });
    addEventListener("keyup", (event) => this.onKey(event, false), { capture: true });
    addEventListener("blur", () => this.releaseAll("blur"));
    document.addEventListener("visibilitychange", () => {
      if (document.hidden) this.releaseAll("hidden");
    });
    this.bindPointers();
  }

  /** Apply an action state to the Game; hold actions are de-duplicated so the Game only sees real transitions. */
  setAction(action, on) {
    const game = this.game;
    switch (action) {
      case "flipL":
      case "flipR":
      case "flipU": {
        if (this.active[action] === on) return;
        this.active[action] = on;
        game.onFlipper(action === "flipL" ? "L" : action === "flipR" ? "R" : "U", on);
        break;
      }
      case "plunge": {
        if (this.active.plunge === on) return;
        this.active.plunge = on;
        game.onPlunge(on);
        break;
      }
      case "start":
        if (on) game.onStart();
        break;
      case "nudgeL":
        if (on) this.nudge(0.9, 0.25);
        break;
      case "nudgeR":
        if (on) this.nudge(-0.9, 0.25);
        break;
      case "nudgeU":
        if (on) this.nudge(0, 0.9);
        break;
    }
    this.hooks.onAnyInput?.();
    this.hooks.onControlState?.({...this.active});
  }

  /** Recompute a hold action from all sources (keys, pointers, gamepad) and push the merged state. */
  refreshAction(action) {
    const keyHeld = this.binds[action].some((code) => this.down.has(code));
    let pointerHeld = false;
    for (const pointerAction of this.pointers.values()) if (pointerAction === action) pointerHeld = true;
    const gamepadHeld =
      action === "flipL" ? this.gpFlipL
      : action === "flipR" ? this.gpFlipR
      : action === "plunge" ? this.gpPlunge
      : false;
    const localHeld = keyHeld || pointerHeld || gamepadHeld;
    if (action === 'flipL' || action === 'flipR') this.updateSecondStage(action, localHeld);
    this.setAction(action, localHeld || this.remote.has(action) || (action === 'flipU' && this.secondStages.size > 0));
  }

  // Local lower-flipper hold is the keyboard/touch fallback for a two-stage physical switch.
  // Remote demonstration stages remain explicit: a first-stage message never forces the upper stage.
  updateSecondStage(action, held) {
    if (held && !this.stageTimers.has(action)) {
      const timer = setTimeout(() => {
        if (this.hooks.inputBlocked?.() || this.hooks.isPaused?.()) return;
        this.secondStages.add(action);
        this.refreshAction('flipU');
      }, 500);
      this.stageTimers.set(action, timer);
    } else if (!held) {
      clearTimeout(this.stageTimers.get(action));
      this.stageTimers.delete(action);
      if (this.secondStages.delete(action)) this.refreshAction('flipU');
    }
  }

  setRemoteAction(action, pressed) {
    if (!Object.hasOwn(DEFAULT_BINDS, action) || typeof pressed !== 'boolean') return false;
    if (pressed && (this.hooks.inputBlocked?.() || this.hooks.isPaused?.())) return false;
    const wasHeld = this.remote.has(action);
    if (pressed) this.remote.add(action); else this.remote.delete(action);
    if (HOLD_ACTIONS.includes(action)) this.refreshAction(action);
    else if (pressed && !wasHeld) this.setAction(action, true);
    return true;
  }

  releaseRemote() {
    // A disconnected host is cancellation, not a real plunger release/launch.
    if (this.remote.has('plunge') && !this.binds.plunge.some(code=>this.down.has(code)) && ![...this.pointers.values()].includes('plunge') && !this.gpPlunge) this.game.plungePower = 0;
    this.remote.clear();
    for (const action of HOLD_ACTIONS) this.refreshAction(action);
  }

  /**
   * Drop every held source and release all hold actions (focus loss, pause, remap, context menu...). None of these
   * releases is the player letting go of the plunger, so a pull in progress is cancelled (power 0) rather than
   * launching the ball into a paused or unwatched game. `reason` only documents the call site (kept in the
   * signature so the callers read as intent, e.g. releaseAll("pause:blur")); nothing branches on it.
   */
  releaseAll(reason = "") {
    for (const timer of this.stageTimers.values()) clearTimeout(timer);
    this.stageTimers.clear();
    this.secondStages.clear();
    this.remote.clear();
    this.down.clear();
    this.pointers.clear();
    this.gpFlipL = this.gpFlipR = this.gpPlunge = false;
    if (this.active.plunge) this.game.plungePower = 0;
    for (const action of HOLD_ACTIONS) this.setAction(action, false);
  }

  /** Action bound to a key code, or null. */
  actionOf(code) {
    for (const [action, codes] of Object.entries(this.binds)) if (codes.includes(code)) return action;
    return null;
  }

  // ---- rebinding ---------------------------------------------------------------------------------------------

  /** Arm a remap: the next accepted key press becomes the primary binding of `action`. */
  beginCapture(action) {
    this.captureFor = action in this.binds ? action : null;
  }

  /** Forget a pending remap without touching the bindings (help panel closed, viewer opened...). */
  cancelCapture() {
    this.captureFor = null;
  }

  /**
   * Make `code` the primary binding of `action`, removing it from every other action so one key never drives two
   * actions (actionOf would otherwise silently prefer the first). An action left without keys gets its defaults
   * back (minus codes in use elsewhere). Returns 'bound' or 'reserved' (code rejected, nothing changed).
   */
  bindCode(action, code) {
    if (!Input.isBindableCode(code)) return "reserved";
    for (const codes of Object.values(this.binds)) {
      const index = codes.indexOf(code);
      if (index >= 0) codes.splice(index, 1);
    }
    this.binds[action] = [code, ...this.binds[action]].slice(0, 4);
    Input.fillEmptyActions(this.binds);
    return "bound";
  }

  /** Remap capture: Escape cancels, reserved keys are refused (capture stays armed), anything else is bound. */
  captureKey(event) {
    const action = this.captureFor;
    event.preventDefault();
    if (event.code === "Escape") {
      this.captureFor = null;
      this.hooks.onRemapped?.(action, "cancelled");
      return;
    }
    const outcome = this.bindCode(action, event.code);
    if (outcome === "bound") {
      this.captureFor = null;
      this.releaseAll("remap");
    }
    this.hooks.onRemapped?.(action, outcome);
  }

  // ---- keyboard ----------------------------------------------------------------------------------------------

  /**
   * 'text' when a text field owns the keyboard (every key belongs to it), 'button' for a focused button/link
   * (only Enter, Space and Tab belong to it), else null.
   */
  focusedControl() {
    const element = document.activeElement;
    if (!element || element === document.body) return null;
    if (element.matches?.("input, textarea, select, [contenteditable]:not([contenteditable=false])")) return "text";
    if (element.matches?.("button, a[href], [role=button], summary")) return "button";
    return null;
  }

  /** True when the key press must reach the focused control instead of the game. */
  keyBelongsToFocus(event) {
    const control = this.focusedControl();
    if (control === "text") return true;
    if (control === "button") return ["Enter", "NumpadEnter", "Space", "Tab"].includes(event.code);
    return false;
  }

  /**
   * Workaround: browsers/OSes can swallow the keyup of Shift/Control (OS shortcuts, focus changes, macOS key
   * combos). Whenever any other key event reports the modifier as NOT held, forget it and re-evaluate the
   * flippers bound to it so they do not stay stuck up.
   */
  dropReleasedModifiers(event) {
    if (typeof event.getModifierState != "function") return;
    const droppedCodes = [];
    if (!event.getModifierState("Shift") && event.code !== "ShiftLeft" && event.code !== "ShiftRight") {
      if (this.down.delete("ShiftLeft")) droppedCodes.push("ShiftLeft");
      if (this.down.delete("ShiftRight")) droppedCodes.push("ShiftRight");
    }
    if (!event.getModifierState("Control") && event.code !== "ControlLeft" && event.code !== "ControlRight") {
      if (this.down.delete("ControlLeft")) droppedCodes.push("ControlLeft");
      if (this.down.delete("ControlRight")) droppedCodes.push("ControlRight");
    }
    for (const code of droppedCodes) {
      const droppedAction = this.actionOf(code);
      if (droppedAction && HOLD_ACTIONS.includes(droppedAction)) this.refreshAction(droppedAction);
    }
  }

  /** Unbound single-key UI shortcuts (fresh presses only; never with Ctrl/Cmd/Alt, which belong to the browser). */
  hotkey(event, blocked) {
    switch (event.code) {
      case "KeyP":
        this.hooks.onPause?.();
        return;
      case "KeyV":
        this.hooks.onView?.();
        return;
      case "KeyM":
        this.hooks.onMute?.();
        return;
      case "KeyH":
      case "F1":
        this.hooks.onHelp?.();
        event.preventDefault();
        return;
      case "KeyF":
        this.hooks.onFullscreen?.();
        return;
      case "KeyD":
        this.hooks.onDebug?.();
        return;
      case "KeyR":
        if (!blocked) this.hooks.onRestart?.(); // restarting is a game action: not from inside the help panel
        return;
    }
  }

  /** keydown/keyup handler (capture phase). */
  onKey(event, isDown) {
    if (this.captureFor) {
      if (isDown) this.captureKey(event);
      return;
    }
    this.dropReleasedModifiers(event);
    const blocked = this.hooks.inputBlocked?.() || false;
    if (event.code === "Escape") {
      if (isDown && !event.repeat && this.hooks.onEscape?.()) event.preventDefault();
      return;
    }
    const action = this.actionOf(event.code);
    const focusOwnsKey = this.keyBelongsToFocus(event);
    if (!isDown) {
      // Releases are always honoured (a held action must never survive its key), just never at the expense of a
      // focused control's own key handling.
      if (action) {
        this.down.delete(event.code);
        if (HOLD_ACTIONS.includes(action)) this.refreshAction(action);
        if (!focusOwnsKey && !blocked) event.preventDefault();
      }
      return;
    }
    if (focusOwnsKey) return; // Enter/Space/Tab activate or leave the focused button; text fields keep everything
    if (blocked === "machine") {
      event.preventDefault(); // the machine viewer is modal: only Escape (handled above) gets through
      return;
    }
    if (!action) {
      if (!event.repeat && !event.ctrlKey && !event.metaKey && !event.altKey) this.hotkey(event, blocked);
      return;
    }
    if (event.metaKey) return; // Cmd/Win+key combinations are browser/OS shortcuts, not game input
    if (blocked || this.hooks.isPaused?.()) return; // help panel open: game actions are ignored (keys keep their default behaviour)
    event.preventDefault();
    this.down.add(event.code);
    if (HOLD_ACTIONS.includes(action)) this.refreshAction(action);
    else if (!event.repeat) this.setAction(action, true);
  }

  /**
   * Nudge the table; rate limited (120 ms for keys, 350 ms for the gamepad stick) so key auto-repeat / stick
   * jitter cannot spam tilt warnings.
   */
  nudge(dx, dy, cooldownMs = 120) {
    if (performance.now() < this.nudgeCD) return;
    this.nudgeCD = performance.now() + cooldownMs;
    this.game.onNudge(dx, dy);
  }

  /**
   * Poll the first connected gamepad once per frame: LB/LT = left flipper, RB/RT = right, A = plunge, Start,
   * left stick = nudge. Stick nudges are edge-triggered: one nudge per deflection past 0.8, re-armed once the
   * axis returns below 0.4, so a held or drifting stick cannot tilt the game.
   */
  pollGamepad() {
    if (this._gpDead || this.hooks.inputBlocked?.() || this.hooks.isPaused?.()) return;
    let pads;
    try {
      pads = navigator.getGamepads?.() || [];
    } catch {
      this._gpDead = true;
      return;
    }
    const pad = [...pads].find((candidate) => candidate && candidate.connected);
    if (!pad) {
      this.gpFlipL = this.gpFlipR = this.gpPlunge = false;
      this._gpStart = this._gpNudgeX = this._gpNudgeY = false;
      for (const action of HOLD_ACTIONS) this.refreshAction(action);
      return;
    }
    const flipL = pad.buttons[4]?.pressed || pad.buttons[6]?.value > 0.4;
    const flipR = pad.buttons[5]?.pressed || pad.buttons[7]?.value > 0.4;
    const plunge = !!pad.buttons[0]?.pressed;
    if (flipL !== this.gpFlipL) {
      this.gpFlipL = flipL;
      this.refreshAction("flipL");
    }
    if (flipR !== this.gpFlipR) {
      this.gpFlipR = flipR;
      this.refreshAction("flipR");
    }
    if (plunge !== this.gpPlunge) {
      this.gpPlunge = plunge;
      this.refreshAction("plunge");
    }
    if (pad.buttons[9]?.pressed && !this._gpStart) this.game.onStart();
    this._gpStart = !!pad.buttons[9]?.pressed;
    const stickX = pad.axes[0] || 0;
    const stickY = pad.axes[1] || 0;
    if (Math.abs(stickX) > 0.8) {
      if (!this._gpNudgeX) {
        this._gpNudgeX = true;
        this.nudge(-Math.sign(stickX) * 0.9, 0.2, 350);
      }
    } else if (Math.abs(stickX) < 0.4) {
      this._gpNudgeX = false;
    }
    if (stickY < -0.8) {
      if (!this._gpNudgeY) {
        this._gpNudgeY = true;
        this.nudge(0, 0.9, 350);
      }
    } else if (stickY > -0.4) {
      this._gpNudgeY = false;
    }
  }

  // ---- pointers ----------------------------------------------------------------------------------------------

  /**
   * Pointer/touch controls: left half = left flipper, right half = right flipper, right edge (x > 74%) = plunger
   * while waiting for the first launch (state LAUNCH); any press in ATTRACT/OVER/INITIALS acts as Start. UI chrome,
   * the pause overlay, non-primary mouse buttons and a paused/blocked game are left alone.
   */
  bindPointers() {
    const isOnChrome = (event) =>
      event.target.closest?.("#buttons, #help, #overlay, #nogl, #errorBanner, a, button, input");
    const onPointerDown = (event) => {
      if (event.pointerType === "mouse" && event.button !== 0) return;
      if (isOnChrome(event)) return;
      if (this.hooks.inputBlocked?.() || this.hooks.isPaused?.()) return;
      this.hooks.onAnyInput?.();
      const game = this.game;
      const xFrac = event.clientX / innerWidth;
      if (game.state === "ATTRACT" || game.state === "OVER" || game.state === "INITIALS") {
        if (event.isPrimary !== false) game.onStart();
        return;
      }
      let action;
      if (game.state === "LAUNCH" && game.seatBall && xFrac > 0.74) action = "plunge";
      else action = xFrac < 0.5 ? "flipL" : "flipR";
      this.pointers.set(event.pointerId, action);
      try {
        event.target.setPointerCapture?.(event.pointerId);
      } catch {}
      this.refreshAction(action);
      if (event.cancelable) event.preventDefault();
    };
    const onPointerUp = (event) => {
      const action = this.pointers.get(event.pointerId);
      if (action !== undefined) {
        this.pointers.delete(event.pointerId);
        this.refreshAction(action);
      }
    };
    addEventListener("pointerdown", onPointerDown, { passive: false });
    addEventListener("pointerup", onPointerUp, { capture: true });
    addEventListener("pointercancel", onPointerUp, { capture: true });
    addEventListener("lostpointercapture", onPointerUp, { capture: true });
    // Fallbacks for pointer events that never get their up (mouse/touch end without a matching pointerup).
    addEventListener("mouseup", () => {
      if (this.pointers.size) {
        this.pointers.clear();
        HOLD_ACTIONS.forEach((action) => this.refreshAction(action));
      }
    });
    addEventListener("touchend", (event) => {
      if (event.touches.length === 0 && this.pointers.size) {
        this.pointers.clear();
        HOLD_ACTIONS.forEach((action) => this.refreshAction(action));
      }
      // A touch-started pointerdown is not a user activation for Web Audio; touchend/click are.
      this.hooks.onAnyInput?.();
    });
    addEventListener("click", () => this.hooks.onAnyInput?.());
    // Block page scroll/zoom gestures while playing; the help panel keeps its own scrolling.
    addEventListener(
      "touchmove",
      (event) => {
        if (event.target.closest?.("#help")) return;
        if (event.cancelable) event.preventDefault();
      },
      { passive: false },
    );
    addEventListener("contextmenu", (event) => {
      event.preventDefault();
      this.releaseAll("ctx");
    });
  }
}

/**
 * Codes that can never be bound: keys with a UI meaning (Escape cancels/closes, Tab moves focus), browser keys and
 * the fixed single-key hotkeys (P pause, V view, M mute, H/F1 help, F fullscreen, D debug, R restart).
 */
Input.RESERVED_CODES = new Set([
  "Escape", "Tab", "F1", "F5", "F11", "F12", "MetaLeft", "MetaRight", "OSLeft", "OSRight", "ContextMenu",
  "KeyP", "KeyV", "KeyM", "KeyH", "KeyF", "KeyD", "KeyR",
]);

/** A well-formed, non-reserved KeyboardEvent.code ("KeyZ", "ShiftLeft", "Numpad4"...). */
Input.isBindableCode = (code) =>
  typeof code === "string" && /^[A-Za-z0-9]{1,32}$/.test(code) && code !== "Unidentified" &&
  !Input.RESERVED_CODES.has(code);

/** Give every keyless action its defaults back, skipping codes that another action is using. */
Input.fillEmptyActions = (binds) => {
  for (const action of Object.keys(DEFAULT_BINDS)) {
    if (binds[action].length) continue;
    const used = new Set(Object.values(binds).flat());
    const free = DEFAULT_BINDS[action].filter((code) => !used.has(code));
    binds[action] = free.length ? free : [...DEFAULT_BINDS[action]];
  }
  return binds;
};

/**
 * Bindings from untrusted storage -> a complete, valid bindings object. Per action: an entry must be a non-empty
 * array of strings, else the defaults apply; codes are validated like a live rebind (isBindableCode), de-duplicated
 * within the action and across actions (earlier actions keep the key), and capped at the four slots.
 */
Input.sanitizeBindings = (saved) => {
  const binds = {};
  const used = new Set();
  for (const action of Object.keys(DEFAULT_BINDS)) {
    const entry = saved && typeof saved === "object" ? saved[action] : undefined;
    const wellFormed = Array.isArray(entry) && entry.length > 0 && entry.every((code) => typeof code === "string");
    const codes = [];
    for (const code of wellFormed ? entry : DEFAULT_BINDS[action]) {
      if (codes.length < 4 && Input.isBindableCode(code) && !used.has(code) && !codes.includes(code)) codes.push(code);
    }
    for (const code of codes) used.add(code);
    binds[action] = codes;
  }
  return Input.fillEmptyActions(binds);
};
